'use client';

/**
 * usePermissions Hook
 *
 * Context-first: reads from centralized AppShell state when available (zero fetches).
 * Falls back to standalone fetch when outside AppShellProvider.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import logger from '@/lib/logger';
import { createClient } from '@/lib/supabase/client';
import {
  useOptionalPermissionsState,
  useOptionalAccessControlState,
} from '@/components/shell/AppShellContext';
import type {
  BillingTier,
  Role,
  TierFeatures,
  UsePermissionsResult,
  UseHasPermissionResult,
  UseCanAccessFeatureResult,
} from '@/lib/access-control/types';
import {
  tierHasFeature,
  meetsTierRequirement,
  checkFeatureAccess,
} from '@/lib/access-control/tiers';

// ============================================================================
// API Base URL
// ============================================================================

const API_BASE = '/api/access-control';

// ============================================================================
// Types
// ============================================================================

interface PermissionsState {
  permissions: string[];
  roles: Role[];
  tier: BillingTier;
  loading: boolean;
  error: Error | null;
}

interface ApiRole {
  id: string;
  name: string;
  slug?: string;
  display_name?: string;
  description: string | null;
  scope?: string;
  tenant_org_unit_id?: string | null;
  privilege_level?: number;
  hierarchy_level?: number;
  is_system?: boolean;
  is_system_role?: boolean;
  is_assignable?: boolean;
  is_default?: boolean;
  color?: string | null;
  icon?: string | null;
  created_at: string;
  updated_at: string;
}

function mapApiRoleToRole(apiRole: ApiRole): Role {
  return {
    id: apiRole.id,
    tenant_org_unit_id: apiRole.tenant_org_unit_id ?? null,
    name: apiRole.display_name || apiRole.name,
    slug: apiRole.slug || apiRole.name.toLowerCase().replace(/\s+/g, '-'),
    description: apiRole.description,
    privilege_level: apiRole.privilege_level ?? apiRole.hierarchy_level ?? 0,
    is_system_role: apiRole.is_system_role ?? apiRole.is_system ?? false,
    is_assignable: apiRole.is_assignable ?? true,
    is_default: apiRole.is_default ?? false,
    color: apiRole.color ?? null,
    icon: apiRole.icon ?? null,
    badge_url: (apiRole as any).badge_url ?? null,
    created_at: apiRole.created_at,
    updated_at: apiRole.updated_at,
  };
}

// ============================================================================
// usePermissions Hook
// ============================================================================

export function usePermissions(orgUnitId?: string): UsePermissionsResult {
  // Try centralized state first (zero fetches)
  const centralPerms = useOptionalPermissionsState();
  const useCentral = !!centralPerms;

  // IMPORTANT: Always call standalone hooks unconditionally to maintain
  // consistent hook order across renders. React requires the same hooks
  // to be called in the same order every render. The `enabled` flag
  // skips the actual fetch when centralized state is available.
  const standalone = usePermissionsStandalone(orgUnitId, !useCentral);

  // When centralized state is available and loaded, return it directly
  // (no hooks in this branch — just pure object construction)
  if (useCentral) {
    const perms = centralPerms!.permissions;
    return {
      permissions: perms,
      roles: centralPerms!.roles,
      loading: centralPerms!.loading,
      error: centralPerms!.error,
      hasPermission: (code: string) => perms.includes(code),
      hasAnyPermission: (codes: string[]) => codes.some(c => perms.includes(c)),
      hasAllPermissions: (codes: string[]) => codes.every(c => perms.includes(c)),
      refresh: async () => {},
    };
  }

  return standalone;
}

/**
 * Standalone version that fetches its own data
 */
function usePermissionsStandalone(orgUnitId?: string, enabled = true): UsePermissionsResult {
  const [state, setState] = useState<PermissionsState>({
    permissions: [],
    roles: [],
    tier: 'trial',
    loading: true,
    error: null,
  });

  const supabase = useMemo(() => createClient(), []);

  const fetchPermissions = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError) {
        throw new Error(`Authentication error: ${authError.message}`);
      }

      if (!user) {
        setState({ permissions: [], roles: [], tier: 'trial', loading: false, error: null });
        return;
      }

      const params = new URLSearchParams();
      if (orgUnitId) params.set('org_unit_id', orgUnitId);

      const response = await fetch(`${API_BASE}/context?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error('Not authenticated');
        if (response.status === 403) throw new Error('Access denied');
        throw new Error(`Failed to fetch permissions: ${response.statusText}`);
      }

      const data = await response.json();

      setState({
        permissions: data.platform_permissions || [],
        roles: (data.platform_roles || []).map(mapApiRoleToRole),
        tier: data.effective_tier || 'trial',
        loading: false,
        error: null,
      });
    } catch (err) {
      logger.error('usePermissions', 'Error fetching permissions', { err });
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err : new Error('Failed to fetch permissions'),
      }));
    }
  }, [supabase, orgUnitId]);

  const hasPermission = useCallback(
    (code: string): boolean => state.permissions.includes(code),
    [state.permissions]
  );

  const hasAnyPermission = useCallback(
    (codes: string[]): boolean => codes.some(c => state.permissions.includes(c)),
    [state.permissions]
  );

  const hasAllPermissions = useCallback(
    (codes: string[]): boolean => codes.every(c => state.permissions.includes(c)),
    [state.permissions]
  );

  const refresh = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    if (!enabled) return;
    fetchPermissions();
  }, [fetchPermissions, enabled]);

  return {
    permissions: state.permissions,
    roles: state.roles,
    loading: state.loading,
    error: state.error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refresh,
  };
}

// ============================================================================
// useHasPermission Hook
// ============================================================================

export function useHasPermission(
  permissionCode: string,
  orgUnitId?: string
): UseHasPermissionResult {
  const { permissions, loading, error } = usePermissions(orgUnitId);

  const allowed = useMemo(() => {
    return permissions.includes(permissionCode);
  }, [permissions, permissionCode]);

  return { allowed, loading, error };
}

// ============================================================================
// useCanAccessFeature Hook
// ============================================================================

export function useCanAccessFeature(
  feature: keyof TierFeatures
): UseCanAccessFeatureResult {
  // Try centralized state first to avoid extra /api/access-control/tier fetch
  const centralAC = useOptionalAccessControlState();
  const useCentral = !!centralAC;

  // Always call standalone hooks for consistent hook order
  const standalone = useCanAccessFeatureStandalone(feature, !useCentral);

  if (useCentral) {
    const context = centralAC!.userContext;
    const tier = context?.effective_tier || 'trial';
    const access = checkFeatureAccess(tier, feature);
    return {
      allowed: Boolean(context) && !centralAC!.error && access.allowed,
      loading: centralAC!.loading,
      error: centralAC!.error,
      required_tier: access.required_tier,
      upgrade_url: access.upgrade_url,
    };
  }

  return standalone;
}

function useCanAccessFeatureStandalone(
  feature: keyof TierFeatures,
  enabled = true
): UseCanAccessFeatureResult {
  const [state, setState] = useState<UseCanAccessFeatureResult>({
    allowed: false,
    loading: true,
    error: null,
  });

  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    if (!enabled) return;
    const checkFeature = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError) throw new Error(`Authentication error: ${authError.message}`);

        if (!user) {
          setState({ allowed: false, loading: false, error: null });
          return;
        }

        const response = await fetch(`${API_BASE}/tier`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch tier: ${response.statusText}`);
        }

        const data = await response.json();
        const userTier: BillingTier = data.tier || 'trial';
        const access = checkFeatureAccess(userTier, feature);

        setState({
          allowed: access.allowed,
          loading: false,
          error: null,
          required_tier: access.required_tier,
          upgrade_url: access.upgrade_url,
        });
      } catch (err) {
        logger.error('useCanAccessFeature', 'Error', { err });
        setState({
          allowed: false,
          loading: false,
          error: err instanceof Error ? err : new Error('Failed to check feature access'),
        });
      }
    };

    checkFeature();
  }, [supabase, feature, enabled]);

  return state;
}

// ============================================================================
// Utility Hooks
// ============================================================================

export function usePermissionChecks(
  permissionCodes: string[],
  orgUnitId?: string
): Record<string, boolean> & { loading: boolean } {
  const { permissions, loading } = usePermissions(orgUnitId);

  const checks = useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const code of permissionCodes) {
      result[code] = permissions.includes(code);
    }
    return { ...result, loading };
  }, [permissions, permissionCodes, loading]);

  return checks as Record<string, boolean> & { loading: boolean };
}

export { useIsAdmin } from '@/hooks/useIsAdmin';

export default usePermissions;
